export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-200 px-6 py-12">
      <div className="max-w-4xl mx-auto">
        <div className="font-mono text-xs text-[#ccff00] uppercase tracking-wider mb-3">
          TuneAmplifier
        </div>

        <h1 className="font-display text-4xl lg:text-5xl font-bold mb-3">
          Privacy Policy
        </h1>

        <p className="text-sm text-zinc-500 mb-10">
          Last updated: August 17, 2026
        </p>

        <div className="space-y-8 text-sm leading-7 text-zinc-300">
          <section>
            <p>
              TuneAmplifier ("TuneAmplifier", "we", "us", or "our") operates
              the TuneAmplifier website and services available at
              https://tuneamplifier.com.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-display font-bold text-white mb-3">
              1. Information We Collect
            </h2>
            <p className="mb-3">
              When you create an account or use TuneAmplifier, we may collect:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Name</li>
              <li>Email address</li>
              <li>Account authentication information</li>
              <li>Song titles and artist names</li>
              <li>Music and playlist links</li>
              <li>Lyrics or other information you voluntarily provide</li>
              <li>Images, videos, and other uploaded or generated media</li>
              <li>Technical and usage information</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-display font-bold text-white mb-3">
              2. Connected Third-Party Accounts
            </h2>
            <p>
              When you connect a third-party service, TuneAmplifier may receive
              information permitted by that service's authorization process,
              including profile information, account identifiers, connected
              pages or channels, music, videos, playlists, and analytics where
              permitted.
            </p>
            <p className="mt-3">
              Supported services may include Spotify, YouTube, Facebook,
              Instagram, TikTok, X, Google Ads, Meta Ads, TikTok Ads, and other
              services supported by TuneAmplifier.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-display font-bold text-white mb-3">
              3. How We Use Information
            </h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>Provide and operate TuneAmplifier</li>
              <li>Authenticate users</li>
              <li>Connect authorized third-party services</li>
              <li>Import songs, playlists, videos, and other authorized content</li>
              <li>Generate social media and advertising content</li>
              <li>Publish content to platforms you authorize</li>
              <li>Create and manage authorized advertising campaigns</li>
              <li>Provide analytics and reporting</li>
              <li>Maintain and improve the service</li>
              <li>Prevent fraud, abuse, and unauthorized access</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-display font-bold text-white mb-3">
              4. Third-Party Services
            </h2>
            <p>
              TuneAmplifier integrates with third-party services. Information
              accessed through those services may also be subject to the
              relevant provider's own privacy policy and terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-display font-bold text-white mb-3">
              5. OAuth Access
            </h2>
            <p>
              When you connect a third-party account through OAuth,
              TuneAmplifier receives only the permissions you authorize.
              Access tokens and, where applicable, refresh tokens are stored
              securely on our servers and used to maintain the authorized
              connection and provide the requested functionality.
            </p>
            <p className="mt-3">
              You may revoke access through the relevant third-party provider
              or disconnect the account through TuneAmplifier where available.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-display font-bold text-white mb-3">
              6. Data Sharing
            </h2>
            <p>
              We do not sell your personal information. Information may be
              transmitted to service providers and third-party APIs when
              necessary to provide TuneAmplifier functionality, process actions
              you authorize, operate the service, or comply with legal
              obligations.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-display font-bold text-white mb-3">
              7. Data Retention
            </h2>
            <p>
              We retain information for as long as necessary to provide the
              service, maintain accounts, comply with legal obligations,
              resolve disputes, and enforce agreements.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-display font-bold text-white mb-3">
              8. Data Deletion
            </h2>
            <p>
              You may request deletion of your TuneAmplifier account and
              associated personal information. See our Data Deletion page for
              instructions:
            </p>
            <a
              href="/data-deletion"
              className="inline-block mt-3 text-[#ccff00] hover:underline"
            >
              https://tuneamplifier.com/data-deletion
            </a>
          </section>

          <section>
            <h2 className="text-xl font-display font-bold text-white mb-3">
              9. Security
            </h2>
            <p>
              We use reasonable technical and organizational measures designed
              to protect information against unauthorized access, alteration,
              disclosure, or destruction. No internet-based service can
              guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-display font-bold text-white mb-3">
              10. Children's Privacy
            </h2>
            <p>
              TuneAmplifier is not directed to children under the minimum age
              required by applicable law. We do not knowingly collect personal
              information from children in violation of applicable law.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-display font-bold text-white mb-3">
              11. Your Rights
            </h2>
            <p className="mb-3">
              Depending on your jurisdiction, you may have rights to access,
              correct, delete, restrict, object to certain processing of, or
              request a copy of your personal information.
            </p>
            <p>
              Contact us at{' '}
              <a
                href="mailto:tuneamplifier@gmail.com"
                className="text-[#ccff00] hover:underline"
              >
                tuneamplifier@gmail.com
              </a>
              .
            </p>
          </section>

          <section>
            <h2 className="text-xl font-display font-bold text-white mb-3">
              12. Changes to This Policy
            </h2>
            <p>
              We may update this Privacy Policy from time to time. The updated
              version will be published on this page with a revised
              "Last updated" date.
            </p>
          </section>

          <section className="border-t border-zinc-800 pt-6">
            <h2 className="text-xl font-display font-bold text-white mb-3">
              13. Contact
            </h2>
            <p>
              TuneAmplifier
              <br />
              tuneamplifier@gmail.com
              <br />
              https://tuneamplifier.com
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
